import {
  faChevronDown,
  faChevronUp,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import kebabCase from "lodash/kebabCase";
import useClickOutside from "../useClickOutside";
import useKeyboardNavigation from "../hooks/useKeyboardNavigation";

interface SelectFieldOption {
  id: number;
  title: string;
  subTitle: string;
  warning: string;
}

interface SelectFieldProps {
  options: SelectFieldOption[];
  selectedOption: number;
  handleChangeSelectedOption: (option: SelectFieldOption) => void;
  className?: string;
  dropdownWidth?: string;
  dataTestId?: string;
}

export function SelectField(props: SelectFieldProps) {
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => setExpanded(false));

  const selectItemByIndex = (index: number) => {
    if (index >= 0 && index < props.options.length) {
      selectOption(props.options[index]);
    }
  };
  const selectOption = (option: SelectFieldOption) => {
    props.handleChangeSelectedOption(option);
    setExpanded(false);
  };

  const [keyboardNavigationState] = useKeyboardNavigation(
    props.options.length,
    0,
    selectItemByIndex,
    expanded,
    () => setExpanded(!expanded),
    wrapperRef.current!,
  );

  let selectedOption: SelectFieldOption | undefined = undefined;
  if (props.selectedOption) {
    selectedOption = props.options.filter(
      (opt) => opt.id === props.selectedOption,
    )[0];
  }

  const icon = expanded ? faChevronUp : faChevronDown;

  return (
    <div ref={wrapperRef} tabIndex={0}>
      <div
        className={`tw-border-2 tw-bg-white ${
          expanded
            ? "tw-border-solid tw-border-blue-2"
            : "tw-border-solid tw-border-gray-light-4"
        } tw-flex tw-max-w-[400px] tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-p-3 tw-text-black ${props.className} `}
        onClick={() => setExpanded(!expanded)}
        data-testid={props.dataTestId || undefined}
      >
        <span>{selectedOption ? selectedOption.title : "(Please select)"}</span>
        <FontAwesomeIcon icon={icon} className="tw-shrink-0" />
      </div>
      {expanded && (
        <div
          className={`tw-t-12 tw-border-1 tw-absolute tw-rounded-md tw-border-solid tw-border-gray-light-4 tw-bg-white tw-shadow-md tw-w-[${props.dropdownWidth || "400"}px] tw-z-10 tw-cursor-pointer tw-px-1`}
        >
          {props.options.map((option, i) => {
            const kebabCaseTitle = kebabCase(option.title) || option.id;
            const optionDataTestId = props.dataTestId
              ? `${props.dataTestId}-select-field-option-${kebabCaseTitle}`
              : undefined;
            return (
              <div
                key={`${option.id}-${i}`}
                className={`tw-p-3 ${
                  i === keyboardNavigationState.selectedIndex
                    ? "tw-bg-gray-light-4"
                    : "tw-bg-white"
                } tw-my-1 tw-flex tw-w-full tw-justify-stretch tw-rounded-md hover:tw-bg-gray-light-4`}
                onClick={() => {
                  selectOption(option);
                }}
                data-testid={optionDataTestId}
              >
                <div className="tw-table-cell tw-align-middle">
                  {option.title.length > 0 && (
                    <span>
                      {option.title}
                      <br />
                    </span>
                  )}
                  {option.subTitle.length > 0 && (
                    <span className="tw-text-gray-3">
                      {option.subTitle}
                      <br />
                    </span>
                  )}
                  {option.warning.length > 0 && (
                    <span className="tw-flex tw-items-center tw-text-orange-1">
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="tw-mr-2 tw-h-[16px] tw-w-[16px]"
                      />
                      {option.warning}
                      <br />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
